import { Square } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../../components/Button';
import InlineErrorDisplay from '../../../../components/InlineErrorDisplay';
import { ArmLabel } from '../ArmLabel';
import { DraftPromptModal } from '../DraftPromptModal';
import { isActiveRun, type EvalModel, type EvalRunItem, type PromptOverride } from '../../types/evalTypes';
import { COST_NOTE, formatUsd, runCost } from '../../utils/evalCost';
import { formatDateTime, formatDuration, modelLabel } from '../../utils/evalFormat';
import { RunStatusBadge } from '../RunStatusBadge';

interface RunHeaderProps {
  run: EvalRunItem;
  models: EvalModel[];
  /** For the production badge. */
  productionModel: string | undefined;
  /** The draft prompt text of each arm that had one. */
  prompts: PromptOverride[];
  /** Throws on failure. */
  onCancel: () => Promise<void>;
}

export const RunHeader = ({ run, models, productionModel, prompts, onCancel }: RunHeaderProps) => {
  const [viewingArmId, setViewingArmId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const viewing = prompts.find((prompt) => prompt.armId === viewingArmId);
  const cost = runCost(models, run);
  const active = isActiveRun(run.status);
  const progress = run.total > 0 ? Math.round((run.resultCount / run.total) * 100) : 0;

  const cancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await onCancel();
    } catch (caught) {
      setCancelError(caught instanceof Error ? caught.message : 'Failed to cancel the run.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">
          <span className="font-mono">{run.suiteId}</span>
          <span className="text-base font-normal text-gray-500 ml-2">{formatDateTime(run.startedAt)}</span>
        </h1>
        <RunStatusBadge run={run} />
      </div>

      {active && (
        <div className="flex flex-wrap items-center gap-3">
          <div
            className="relative flex-1 min-w-48 h-3 bg-gray-100 rounded-full overflow-hidden"
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={`h-full rounded-full transition-[width] duration-700 ${run.cancelRequested ? 'bg-amber-400' : 'bg-blue-500'}`}
              style={{ width: `${progress}%` }}
            />
            {/* A band moves along the whole bar, so a run that is waiting or between trials still shows it is alive. */}
            {!run.cancelRequested && (
              <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-blue-200/90 to-transparent motion-safe:animate-progress-sweep" />
            )}
          </div>
          <span className="text-sm text-gray-600">
            {run.resultCount} of {run.total} trials. This page refreshes on its own; you can close it.
          </span>
          <Button
            variant="outline"
            size="sm"
            icon={<Square size={12} />}
            onClick={() => void cancel()}
            disabled={cancelling || run.cancelRequested}
            loading={cancelling}
          >
            {run.cancelRequested ? 'Cancelling…' : 'Cancel run'}
          </Button>
        </div>
      )}
      {cancelError && <InlineErrorDisplay message={cancelError} onDismiss={() => setCancelError(null)} />}
      {run.status === 'failed' && run.error && <p className="text-sm text-red-700">{run.error}</p>}

      <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
        <div>
          <dt className="text-xs text-gray-500">Prompt</dt>
          <dd className="font-mono text-xs">{run.promptKey}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Trials</dt>
          <dd>
            {run.resultCount} ({run.caseIds.length} cases × {run.arms.length} arms × {run.trials})
          </dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Duration</dt>
          <dd>{active ? '–' : formatDuration(run.durationMs)}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Judge</dt>
          <dd>{modelLabel(models, run.judgeModel)}</dd>
          {run.judgePrompts.map((judge) => (
            <dd key={judge.judgeName} className="text-xs text-gray-500">
              <Link to={`/evals/judge-prompts?suite=${run.suiteId}&judge=${judge.judgeName}`} className="hover:underline" title={`Prompt hash ${judge.hash}`}>
                {judge.judgeName} prompt v{judge.number}
              </Link>
              {!judge.wasCurrent && (
                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-800" title="This version was not current when the run started">
                  not current
                </span>
              )}
            </dd>
          ))}
        </div>
        <div title={COST_NOTE}>
          <dt className="text-xs text-gray-500">Est. model cost</dt>
          <dd className="text-gray-700">{formatUsd(cost.model)}</dd>
        </div>
        <div title={COST_NOTE}>
          <dt className="text-xs text-gray-500">Est. judge cost</dt>
          <dd className="text-gray-700">{formatUsd(cost.judge)}</dd>
        </div>
      </dl>

      <div>
        <h2 className="text-xs text-gray-500 mb-1">Arms</h2>
        <ul className="flex flex-wrap gap-2">
          {run.arms.map((arm) => (
            <li key={arm.id} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
              <ArmLabel
                arm={arm}
                models={models}
                productionModel={productionModel}
                onShowDraft={prompts.some((prompt) => prompt.armId === arm.id) ? () => setViewingArmId(arm.id) : undefined}
              />
            </li>
          ))}
        </ul>
      </div>

      <DraftPromptModal armId={viewingArmId} prompt={viewing} onClose={() => setViewingArmId(null)} />
    </div>
  );
};
